import { NavLink, Outlet } from "react-router-dom";
import { BrandLogo } from "../ui/BrandLogo.js";

/**
 * Authenticated Pro Portal shell (build-plan unit 15). Bottom-nav pattern,
 * mobile-first, mirroring the customer AppShell. Two tabs for now —
 * Opportunities and Profile — populated for real in units 16/17.
 *
 * Capability gating happens inside the tabs (via /portal/me), not here:
 * an unclaimed or un-approved account still gets the shell, with the
 * Opportunities tab rendering the "application pending" experience.
 */

// "Invites" not "Jobs": unit 18 introduces actual Jobs (post-selection
// work), which will want that name.
const navItems = [
  { to: "/portal", label: "Invites", end: true, icon: BriefcaseIcon },
  { to: "/portal/quotes", label: "Quotes", end: false, icon: TagIcon },
  { to: "/portal/inbox", label: "Inbox", end: false, icon: ChatIcon },
  { to: "/portal/profile", label: "Profile", end: false, icon: UserIcon },
] as const;

export function PortalShell() {
  return (
    <div className="flex min-h-dvh flex-col bg-field-gray">
      <header className="sticky top-0 z-10 border-b border-charcoal/15 bg-white/95 backdrop-blur pt-[env(safe-area-inset-top)]">
        <div className="mx-auto flex h-14 max-w-md items-center gap-3 px-4">
          <BrandLogo variant="shield" />
          <p className="text-sm font-bold uppercase tracking-wide text-charcoal">Pro Portal</p>
        </div>
      </header>
      <main className="flex-1 overflow-y-auto pb-24">
        <Outlet />
      </main>
      <nav
        aria-label="Primary"
        className="fixed inset-x-0 bottom-0 z-10 border-t border-charcoal/15 bg-white/95 backdrop-blur pb-[env(safe-area-inset-bottom)]"
      >
        <ul className="mx-auto flex max-w-md justify-around">
          {navItems.map(({ to, label, end, icon: Icon }) => (
            <li key={to} className="flex-1">
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  [
                    "flex min-h-[60px] flex-col items-center justify-center gap-1 px-2 py-2 text-xs font-medium",
                    isActive ? "text-brand-700" : "text-charcoal hover:text-charcoal",
                  ].join(" ")
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={[
                        "flex h-7 w-12 items-center justify-center rounded-full transition-colors",
                        isActive ? "bg-brand-50" : "bg-transparent",
                      ].join(" ")}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <span>{label}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

function BriefcaseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  );
}

function TagIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20.59 13.41 11 3.83A2 2 0 0 0 9.59 3.24H4a1 1 0 0 0-1 1v5.59c0 .53.21 1.04.59 1.41l9.58 9.59a2 2 0 0 0 2.83 0l4.59-4.59a2 2 0 0 0 0-2.83Z" />
      <circle cx="7.5" cy="7.5" r="1" />
    </svg>
  );
}

function ChatIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
